import { CSSProperties, FC, KeyboardEvent, MouseEvent, useMemo } from 'react'
import {
	ActionButton,
	Container,
	ExerciseText,
	ExerciseTitle,
	PrimaryMuscle,
	SecondaryMuscleSeparator,
	SecondaryMuscles,
} from './card.styles'
import { Image } from './image.ui'
import { Exercise } from '@entities/exercise'
import { trainingConstructorStore } from '../../stores'
import { observer } from 'mobx-react-lite'
import { MuscleService } from '@entities/muscle/model/service'
import { getMediaUrl } from '@shared/helpers/getMediaUrl'
import { Check, Plus } from 'lucide-react'
import { useIonRouter } from '@ionic/react'

interface ExerciseToAddProps {
	exercise: Exercise
	styles?: CSSProperties
	onClick?: () => void
}

export const ExerciseToAddCard: FC<ExerciseToAddProps> = observer(
	({ exercise, styles, onClick }) => {
		const nav = useIonRouter()
		const isAdded = trainingConstructorStore.isExerciseAdded(
			exercise.id_exercise,
		)

		const handleOpenExercise = () => {
			nav.push(`/exercise/${exercise.id_exercise}`)
		}

		const handleToggle = () => {
			trainingConstructorStore.toggleExercise(exercise)
			onClick?.()
		}

		const handleActionClick = (e: MouseEvent<HTMLButtonElement>) => {
			e.stopPropagation()
			handleToggle()
		}

		const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
			if (e.key === 'Enter' || e.key === ' ') {
				e.preventDefault()
				handleOpenExercise()
			}
		}

		const muscleGroups = useMemo(() => {
			return MuscleService.getMuscleLabelsParentsInfo(
				exercise.muscle_group_codes,
			)
		}, [exercise.muscle_group_codes])

		const primaryMuscle = muscleGroups[0]?.name
		const secondaryMuscles = muscleGroups
			.slice(1, 4)
			.map((muscle) => muscle.name)

		return (
			<Container
				$selected={isAdded}
				style={styles}
				onClick={handleOpenExercise}
				onKeyDown={handleKeyDown}
				role="button"
				tabIndex={0}
				aria-label={`Открыть упражнение ${exercise.name}`}
			>
				<Image src={getMediaUrl(exercise.media.icon)} />
				<ExerciseText>
					<ExerciseTitle>{exercise.name}</ExerciseTitle>
					{primaryMuscle && <PrimaryMuscle>{primaryMuscle}</PrimaryMuscle>}
					{secondaryMuscles.length > 0 && (
						<SecondaryMuscles>
							{secondaryMuscles.map((muscle, index) => (
								<span key={muscle}>
									{index > 0 && (
										<SecondaryMuscleSeparator aria-hidden="true">
											•
										</SecondaryMuscleSeparator>
									)}
									{muscle}
								</span>
							))}
						</SecondaryMuscles>
					)}
				</ExerciseText>
				<ActionButton
					type="button"
					$selected={isAdded}
					onClick={handleActionClick}
					aria-pressed={isAdded}
					aria-label={isAdded ? 'Убрать упражнение' : 'Добавить упражнение'}
				>
					{isAdded ? <Check aria-hidden="true" /> : <Plus aria-hidden="true" />}
				</ActionButton>
			</Container>
		)
	},
)
